import { string } from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Text, Stack, Shelf, ButtonGroup, Card } from '@chaser/components';
import usePush from '../../../../hooks/usePush';
import useFetch from '../../../../hooks/useFetch';
import SettingsService from '../../../../services/settings-service';
import QuickBooksButton from '../../../../components/QuickbooksButton/index.jsx';
import {
  ConnectToXeroButton,
  DisconnectFromXeroButton,
} from '../../../../components/XeroButton/index.jsx';
import ConnectService from '../../../../services/connect-service';
import {
  IntegrationDisplayNames,
  Integrations,
  CSVIntegrationTypes,
} from '../../../../util/constants';
import getIntegrationIcon from './getIntegrationIcon';
import useModal from '../../../../hooks/useModal';
import CSVModal from '../../../connect/components/CSVModal';
import useOrganisation from '../../../../hooks/useOrganisation';
import OdooModal from '../../../connect/components/OdooModal';
import AccountsIQModal from '../../../connect/components/AccountsIQModal';
import StripeModal from '../../../connect/components/StripeModal';
import SageIntacctModal from '../../../connect/components/SageIntacctModal';

const AccountingSoftware = ({ ShortCode }) => {
  const { accessToken, OrganisationID } = useOrganisation();
  const { data, upsert } = useFetch(SettingsService.getIntegration, ShortCode);
  const { execute, loading } = usePush(SettingsService.disconnectAccountingSoftware);
  const showModal = useModal();

  async function handleDisconnect() {
    const [err, res] = await execute(ShortCode);
    if (err) return;
    upsert(state => {
      state.accessToken.expired = res;
    });
  }

  const { accountingSoftware } = data;

  if (accountingSoftware === Integrations.OPENAPI) return null;
  const integration = getIntegrationIcon(data.accountingSoftware);

  const name = IntegrationDisplayNames[accountingSoftware];

  const reconnect = async () => {
    const specialHandlers = {
      [Integrations.ODOO]: () => {
        return showModal(<OdooModal ShortCode={ShortCode} odooConfiguration={accessToken} />);
      },
      [Integrations.ACCOUNTSIQ]: () => {
        return showModal(
          <AccountsIQModal companyId={OrganisationID} region={accessToken?.region} />,
        );
      },
      [Integrations.STRIPE]: () => {
        return showModal(<StripeModal />);
      },
      [Integrations.SAGEINTACCT]: () => {
        return showModal(
          <SageIntacctModal
            reconnect
            companyId={accessToken?.companyId}
            userId={accessToken?.userId}
          />,
        );
      },
    };

    if (specialHandlers[accountingSoftware]) {
      return specialHandlers[accountingSoftware]();
    }

    const url = await ConnectService.connect(accountingSoftware, ShortCode);
    window.location = url;
  };

  const getReconnectButton = () => {
    const buttonComponents = {
      [Integrations.QUICKBOOKS]: <QuickBooksButton onClick={reconnect} />,
      [Integrations.XERO]: <ConnectToXeroButton onClick={reconnect} />,
    };

    if (buttonComponents[accountingSoftware]) {
      return buttonComponents[accountingSoftware];
    }

    return (
      <Button onClick={reconnect} appearance="secondary">
        Reconnect to {name}
      </Button>
    );
  };

  const reconnectButton = getReconnectButton();

  const disconnectButton =
    accountingSoftware === Integrations.XERO ? (
      <DisconnectFromXeroButton onClick={handleDisconnect} loading={loading} />
    ) : (
      <Button loading={loading} onClick={handleDisconnect} appearance="secondary">
        Disconnect
      </Button>
    );

  const isManualCSV =
    accountingSoftware === Integrations.CSV && accessToken.type === CSVIntegrationTypes.MANUAL;
  const showConnectOrDisconnect = !isManualCSV;

  const integrationLogo = {
    src: integration,
    width: [Integrations.SAGE200, Integrations.SAGEINTACCT].includes(accountingSoftware) ? 120 : 50,
    height: [Integrations.SAGE200, Integrations.SAGEINTACCT].includes(accountingSoftware) ? 22 : 35,
  };

  return (
    <Card p="large">
      <Stack gap="medium">
        <Text fontWeight="Bold">Integration</Text>
        <Shelf justifyContent="space-between">
          <Shelf>
            <img
              alt=""
              src={integrationLogo.src}
              width={integrationLogo.width}
              height={integrationLogo.height}
            />
            <Stack gap="xsmall">
              <Text>
                {name} {isManualCSV && 'Uploader'}
              </Text>
              <Text
                color="Mid Neutral 1"
                data-testid="integration-synchronized"
                data-trackingid="integration-synchronized"
              >
                Accounting system used to synchronize your data with Chaser.
              </Text>
            </Stack>
          </Shelf>
          <ButtonGroup>
            {showConnectOrDisconnect &&
              (data.accessToken.expired ? reconnectButton : disconnectButton)}
            {accountingSoftware === Integrations.CSV &&
              (accessToken.type === CSVIntegrationTypes.MANUAL ? (
                <Button as={Link} to={'/import/configure'} appearance="secondary">
                  Configure
                </Button>
              ) : (
                <Button
                  loading={loading}
                  onClick={() =>
                    showModal(<CSVModal sftpConfiguration={accessToken} ShortCode={ShortCode} />)
                  }
                  appearance="secondary"
                >
                  Update
                </Button>
              ))}
          </ButtonGroup>
        </Shelf>
      </Stack>
    </Card>
  );
};

AccountingSoftware.propTypes = {
  ShortCode: string.isRequired,
};

export default AccountingSoftware;
